/**
 * Typed error for remote source fetch failures. The `reason` field lets
 * callers branch on CORS vs. non-2xx vs. scheme rejection without string
 * matching on the message.
 */
export class RemoteFetchError extends Error {
    readonly kind = 'RemoteFetchError' as const;
    readonly reason: 'rejected-scheme' | 'cors' | 'network' | 'non-2xx';
    readonly status?: number;

    constructor(
        reason: RemoteFetchError['reason'],
        message: string,
        options?: { status?: number; cause?: unknown },
    ) {
        super(message);
        this.name = 'RemoteFetchError';
        this.reason = reason;
        if (options?.status !== undefined) this.status = options.status;
        if (options?.cause !== undefined) this.cause = options.cause;
    }
}

/**
 * Fetches the text content of an `https:` URL for use as a diagram source.
 *
 * Only `https:` URLs are accepted; anything else throws immediately with
 * `reason: 'rejected-scheme'`. Network errors and CORS failures both throw
 * with `reason: 'cors'` because the browser does not distinguish them from
 * script — the "paste manually" fallback applies to both.
 *
 * @throws {RemoteFetchError}
 */
export async function fetchRemoteSource(url: string): Promise<string> {
    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch (cause) {
        throw new RemoteFetchError('rejected-scheme', `Invalid URL: ${url}`, { cause });
    }
    if (parsed.protocol !== 'https:') {
        throw new RemoteFetchError(
            'rejected-scheme',
            `Only https: URLs are supported, got ${parsed.protocol}`,
        );
    }
    let response: Response;
    try {
        response = await fetch(url);
    } catch (cause) {
        // fetch() throws on network / CORS failures; the browser does not
        // expose which one occurred, so we surface 'cors' — the most common
        // case in a static-SPA context where the host omits the header.
        throw new RemoteFetchError('cors', 'Network or CORS error fetching shared source', {
            cause,
        });
    }
    if (!response.ok) {
        throw new RemoteFetchError('non-2xx', `HTTP ${response.status}`, {
            status: response.status,
        });
    }
    return response.text();
}
